import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Plus, Pencil, Trash2, ArrowLeft, EyeOff } from 'lucide-react'
import { ACTIVE_LANGUAGES, CATEGORY_APPLIES_TO_VALUES } from '@olimhub/shared'
import type { Category, CategoryAppliesTo, ActiveLanguage } from '@olimhub/shared'
import { categoriesApi } from '@/api'
import type { CategoryInput, UpdateCategoryInput } from '@/api/endpoints/categories'
import { useToast } from '@/components/common/Toast'
import { resolveActiveLanguage } from '@/hooks'

const INPUT_STYLE: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1.5px solid var(--border)',
  background: 'var(--bg-sunk)',
  color: 'var(--text-strong)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-body)',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
}

const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-sm)',
  fontWeight: 600,
  color: 'var(--text-muted)',
  marginBottom: 6,
}

const CARD_STYLE: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-card)',
  padding: 16,
  marginBottom: 12,
}

function friendlyDeleteError(err: unknown, t: (key: string) => string): string {
  const status = (err as { response?: { status?: number } })?.response?.status
  if (status === 409) return t('admin_categories.error_in_use')
  return t('common.error')
}

interface CategoryFormState {
  translations: Record<ActiveLanguage, string>
  touched: Set<ActiveLanguage>
  parentId: string | null
  appliesTo: Set<CategoryAppliesTo>
  isActive: boolean
}

function blankForm(): CategoryFormState {
  return {
    translations: { en: '', ru: '', pt: '', es: '' },
    touched: new Set(),
    parentId: null,
    appliesTo: new Set(),
    isActive: true,
  }
}

function formFromCategory(cat: Category): CategoryFormState {
  return {
    translations: { ...cat.translations },
    touched: new Set(ACTIVE_LANGUAGES),
    parentId: cat.parentId,
    appliesTo: new Set(cat.appliesTo ?? []),
    isActive: cat.isActive,
  }
}

export function AdminCategoriesPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const uiLang = resolveActiveLanguage(i18n.language)

  const [editing, setEditing] = useState<Category | 'new' | null>(null)
  const [form, setForm] = useState<CategoryFormState>(blankForm())

  const { data: categoryList, isLoading } = useQuery({
    queryKey: ['admin', 'categories', uiLang],
    queryFn: () => categoriesApi.getAllForAdmin(uiLang),
  })

  const createMutation = useMutation({
    mutationFn: (input: CategoryInput) => categoriesApi.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] })
      void queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success(t('admin_categories.created'))
      setEditing(null)
    },
    onError: () => toast.error(t('common.error')),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCategoryInput }) =>
      categoriesApi.update(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] })
      void queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success(t('admin_categories.saved'))
      setEditing(null)
    },
    onError: () => toast.error(t('common.error')),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesApi.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] })
      void queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success(t('admin_categories.deleted'))
    },
    onError: (err) => toast.error(friendlyDeleteError(err, t)),
  })

  function openNew() {
    setForm(blankForm())
    setEditing('new')
  }

  function openEdit(cat: Category) {
    setForm(formFromCategory(cat))
    setEditing(cat)
  }

  function setTranslation(lang: ActiveLanguage, value: string) {
    setForm((prev) => {
      const touched = new Set(prev.touched)
      touched.add(lang)
      const translations = { ...prev.translations, [lang]: value }
      // EN drives the default for any language the admin hasn't touched yet.
      if (lang === 'en') {
        for (const other of ACTIVE_LANGUAGES) {
          if (other !== 'en' && !prev.touched.has(other)) {
            translations[other] = value
          }
        }
      }
      return { ...prev, translations, touched }
    })
  }

  function toggleAppliesTo(type: CategoryAppliesTo) {
    setForm((prev) => {
      const appliesTo = new Set(prev.appliesTo)
      if (appliesTo.has(type)) appliesTo.delete(type)
      else appliesTo.add(type)
      return { ...prev, appliesTo }
    })
  }

  function handleSave() {
    const input: CategoryInput = {
      translations: form.translations,
      parentId: form.parentId,
      appliesTo: form.appliesTo.size > 0 ? [...form.appliesTo] : null,
    }
    if (editing === 'new') {
      createMutation.mutate(input)
    } else if (editing) {
      updateMutation.mutate({ id: editing.id, input: { ...input, isActive: form.isActive } })
    }
  }

  const isFormValid = ACTIVE_LANGUAGES.every((lang) => form.translations[lang].trim().length > 0)
  const isSaving = createMutation.isPending || updateMutation.isPending

  const parentOptions = (categoryList ?? []).filter((c) => c.id !== (editing !== 'new' ? editing?.id : undefined))

  if (editing) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', paddingBottom: 40 }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button type="button" aria-label="Back" onClick={() => setEditing(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}>
            <ArrowLeft size={20} />
          </button>
          <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-body)', color: 'var(--text-strong)' }}>
            {editing === 'new' ? t('admin_categories.new_category') : t('admin_categories.edit_category')}
          </span>
        </div>

        <div style={{ padding: '12px 12px 0' }}>
          <div style={CARD_STYLE}>
            {ACTIVE_LANGUAGES.map((lang) => (
              <div key={lang} style={{ marginBottom: 14 }}>
                <label style={LABEL_STYLE}>
                  {t(`admin_categories.lang_${lang}`)}
                  {lang === 'en' && ' *'}
                </label>
                <input
                  type="text"
                  value={form.translations[lang]}
                  onChange={(e) => setTranslation(lang, e.target.value)}
                  style={INPUT_STYLE}
                />
              </div>
            ))}
          </div>

          <div style={CARD_STYLE}>
            <div style={{ marginBottom: 14 }}>
              <label style={LABEL_STYLE}>{t('admin_categories.applies_to')}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {CATEGORY_APPLIES_TO_VALUES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleAppliesTo(type)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 'var(--radius-pill)',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-xs)',
                      fontWeight: 600,
                      background: form.appliesTo.has(type) ? 'var(--brand)' : 'var(--surface-2)',
                      color: form.appliesTo.has(type) ? 'var(--on-brand)' : 'var(--text-muted)',
                    }}
                  >
                    {t(`admin_categories.type_${type}`)}
                  </button>
                ))}
              </div>
              <p style={{ margin: '6px 0 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
                {form.appliesTo.size === 0 ? t('admin_categories.applies_to_general') : ''}
              </p>
            </div>

            <div>
              <label style={LABEL_STYLE}>{t('admin_categories.parent')}</label>
              <select
                value={form.parentId ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, parentId: e.target.value || null }))}
                style={INPUT_STYLE}
              >
                <option value="">{t('admin_categories.parent_none')}</option>
                {parentOptions.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.translations[uiLang] ?? cat.translations.en}
                  </option>
                ))}
              </select>
            </div>

            {editing !== 'new' && (
              <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
                  {t('admin_categories.active')}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.isActive}
                  onClick={() => setForm((prev) => ({ ...prev, isActive: !prev.isActive }))}
                  style={{
                    width: 44, height: 26,
                    borderRadius: 'var(--radius-pill)',
                    border: 'none',
                    background: form.isActive ? 'var(--brand)' : 'var(--border-strong)',
                    cursor: 'pointer',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    top: 3,
                    insetInlineStart: form.isActive ? 'calc(100% - 23px)' : 3,
                    width: 20, height: 20,
                    borderRadius: '50%',
                    background: '#fff',
                  }} />
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleSave}
            disabled={!isFormValid || isSaving}
            style={{
              width: '100%',
              padding: '13px 0',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'var(--brand)',
              color: 'var(--on-brand)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-body)',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isFormValid ? 1 : 0.55,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {isSaving && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
            {t('post.save_changes')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 10, padding: '14px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            aria-label="Back"
            onClick={() => navigate('/admin')}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 style={{ margin: 0, fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-body-lg)', color: 'var(--text-strong)' }}>
            {t('admin_categories.title')}
          </h1>
        </div>
        <button
          type="button"
          onClick={openNew}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 'var(--radius-pill)', border: 'none', background: 'var(--brand)', color: 'var(--on-brand)', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-sm)' }}
        >
          <Plus size={16} />
          {t('admin_categories.new_category')}
        </button>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Loader2 size={24} style={{ color: 'var(--text-muted)', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : !categoryList || categoryList.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
          {t('admin_categories.empty')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 12px' }}>
          {categoryList.map((cat) => (
            <div
              key={cat.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: 'var(--surface)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-card)',
                padding: '10px 14px',
                opacity: cat.isActive ? 1 : 0.6,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
                    {cat.translations[uiLang] ?? cat.translations.en}
                  </span>
                  {!cat.isActive && <EyeOff size={13} color="var(--text-faint)" />}
                </div>
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
                  {cat.appliesTo ? cat.appliesTo.map((t2) => t(`admin_categories.type_${t2}`)).join(', ') : t('admin_categories.applies_to_general')}
                </span>
              </div>
              <button
                type="button"
                aria-label="Edit"
                onClick={() => openEdit(cat)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 6, display: 'flex' }}
              >
                <Pencil size={16} />
              </button>
              <button
                type="button"
                aria-label="Delete"
                onClick={() => { if (confirm(t('admin_categories.confirm_delete'))) deleteMutation.mutate(cat.id) }}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 6, display: 'flex' }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
